import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { CalendarClock, Loader2, Plus, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { centsToEurosInput, formatCents, parseEurosToCents } from '../../lib/money'
import { useServiceLines } from '../../lib/service-lines'
import { START_ANCHORS } from './ServiceLineEditor'
import type {
  CreateBillingPlanInput,
  CreateBillingPlanResponse,
  OpportunityServiceLineDto,
} from '@toga/shared'

// F42 — Wizard del plan de facturación de un contrato GANADO.
// Diálogo montado dentro del <main> (id="app-main") vía portal (mismo patrón
// que UploadInvoiceModal en pages/OCR.tsx): `absolute inset-0` cubre SOLO el
// área de contenido, dejando sidebar y topbar visibles.

interface Props {
  opportunityId: string
  opportunityTitle: string
  /**
   * LOTE B — detalle por línea de la oportunidad para prellenar el plan. La
   * recurrencia y el modo de facturación son de cada línea (no del catálogo):
   * las líneas FACTURABLE recurrentes entran como cuota; las FACTURABLE
   * puntuales como hito; las no facturables (probono/pro-equity) se ignoran.
   */
  serviceLines?: OpportunityServiceLineDto[]
  onClose: () => void
  onCreated: () => void
}

interface MilestoneRow {
  title: string
  amount: string // euros como texto ("1.234,56")
  dueDate: string // YYYY-MM-DD
  vat: string // "% IVA" como texto ("21")
}

type Periodicity = 'MONTHLY' | 'BIMONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'YEARLY'
const PERIODICITIES: readonly Periodicity[] = [
  'MONTHLY',
  'BIMONTHLY',
  'QUARTERLY',
  'SEMIANNUAL',
  'YEARLY',
]

interface RecurringRow {
  name: string
  concept: string
  amount: string
  periodicity: Periodicity
  dayOfMonth: string // "1".."28"
  /** Ancla de inicio: 'DATE' (usa startDate) o una fase de ejecución. */
  startAnchor: string
  startDate: string // YYYY-MM-DD (solo si startAnchor = 'DATE')
  quotaCount: string // vacío = indefinida
  vat: string
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function emptyRecurring(): RecurringRow {
  return {
    name: '',
    concept: '',
    amount: '',
    periodicity: 'MONTHLY',
    dayOfMonth: '1',
    startAnchor: 'DATE',
    startDate: todayIso(),
    quotaCount: '',
    vat: '21',
  }
}

/**
 * Normaliza un importe tecleado al formato del idioma ("450" → "450,00").
 *
 * Las filas PRERRELLENADAS desde las líneas de la oportunidad ya vienen por
 * centsToEurosInput y salen con dos decimales; las que añade el usuario a mano
 * se quedaban tal cual las escribió, así que el asistente mezclaba "450,00" y
 * "450" en la misma columna. Se normaliza al salir del campo, no al teclear,
 * para no pelearse con quien está escribiendo.
 *
 * Un texto que no es un importe válido se devuelve intacto: es preferible que
 * el usuario vea su error a que se lo convirtamos en un 0 silencioso (la
 * validación de crear plan ya exige importe > 0).
 */
function normalizeAmountInput(raw: string): string {
  if (!raw.trim()) return ''
  const cents = parseEurosToCents(raw)
  if (cents <= 0) return raw
  return centsToEurosInput(cents)
}

/** "21" | "21,5" → 0.21 | 0.215. Inválido/vacío → 0.21. */
function parseVatFraction(raw: string): number {
  const n = Number(raw.replace(',', '.'))
  if (!Number.isFinite(n) || n < 0 || n > 100) return 0.21
  return n / 100
}

function getContainer(): HTMLElement {
  return document.getElementById('app-main') ?? document.body
}

export default function BillingPlanWizard({
  opportunityId,
  opportunityTitle,
  serviceLines,
  onClose,
  onCreated,
}: Props) {
  const { t } = useTranslation('opportunities')
  const { allLines, nameOf } = useServiceLines()

  const [milestones, setMilestones] = useState<MilestoneRow[]>([])
  const [recurrings, setRecurrings] = useState<RecurringRow[]>([emptyRecurring()])
  const [partnerName, setPartnerName] = useState('')
  const [partnerPct, setPartnerPct] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [prefilled, setPrefilled] = useState(false)

  // LOTE B — prellenado desde el detalle POR LÍNEA de la oportunidad: la
  // recurrencia y el modo de facturación son de cada línea (no del catálogo).
  // Solo las líneas FACTURABLE con importe entran al plan: las recurrentes como
  // cuota (cadencia/día/vigencia de la línea) y las puntuales como hito a 30
  // días. Las probono/pro-equity se ignoran (no facturan). Todo editable antes
  // de crear el plan.
  useEffect(() => {
    if (prefilled || allLines.length === 0) return
    const lines = (serviceLines ?? []).filter(
      (l) => l.billingMode === 'FACTURABLE' && l.valueCents > 0
    )
    if (lines.length === 0) return
    const newMilestones: MilestoneRow[] = []
    const newRecurrings: RecurringRow[] = []
    for (const l of lines) {
      if (l.isRecurring) {
        newRecurrings.push({
          name: nameOf(l.code),
          concept: nameOf(l.code),
          amount: centsToEurosInput(l.valueCents),
          periodicity: (l.periodicity ?? 'MONTHLY') as Periodicity,
          dayOfMonth: String(l.dayOfMonth ?? 1),
          // Ancla de la línea: por fecha (usa recurringStartDate) o por fase.
          startAnchor: l.recurringStartAnchor ?? 'DATE',
          startDate:
            (l.recurringStartAnchor ?? 'DATE') === 'DATE' && l.recurringStartDate
              ? l.recurringStartDate.slice(0, 10)
              : todayIso(),
          quotaCount: l.occurrences != null ? String(l.occurrences) : '',
          vat: '21',
        })
      } else {
        const due = new Date()
        due.setDate(due.getDate() + 30)
        newMilestones.push({
          title: nameOf(l.code),
          amount: centsToEurosInput(l.valueCents),
          dueDate: due.toISOString().slice(0, 10),
          vat: '21',
        })
      }
    }
    if (newMilestones.length > 0) setMilestones(newMilestones)
    if (newRecurrings.length > 0) setRecurrings(newRecurrings)
    setPrefilled(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLines, serviceLines, prefilled])

  const updateMilestone = (idx: number, patch: Partial<MilestoneRow>) => {
    setMilestones((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }
  const updateRecurring = (idx: number, patch: Partial<RecurringRow>) => {
    setRecurrings((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  // ---------- Resumen en vivo ----------
  const summary = useMemo(() => {
    let boundedCents = 0
    for (const m of milestones) {
      boundedCents += parseEurosToCents(m.amount)
    }
    let hasUnlimited = false
    for (const r of recurrings) {
      const perQuota = parseEurosToCents(r.amount)
      if (perQuota <= 0) continue
      const count = Number(r.quotaCount)
      if (r.quotaCount.trim() && Number.isFinite(count) && count > 0) {
        boundedCents += perQuota * Math.floor(count)
      } else {
        hasUnlimited = true
      }
    }
    const pct = Number(partnerPct.replace(',', '.'))
    const pctFraction = Number.isFinite(pct) && pct > 0 ? Math.min(pct, 100) / 100 : 0
    const commissionCents = Math.round(boundedCents * pctFraction)
    return {
      boundedCents,
      hasUnlimited,
      commissionCents,
      netCents: boundedCents - commissionCents,
    }
  }, [milestones, recurrings, partnerPct])

  // ---------- Envío ----------
  const onSubmit = async () => {
    setError(null)
    const validMilestones = milestones.filter(
      (m) => m.title.trim() && parseEurosToCents(m.amount) > 0 && m.dueDate,
    )
    const validRecurrings = recurrings.filter(
      (r) => r.name.trim() && r.concept.trim() && parseEurosToCents(r.amount) > 0,
    )
    if (validMilestones.length === 0 && validRecurrings.length === 0) {
      setError(t('billingPlan.errorEmpty'))
      return
    }
    const pct = Number(partnerPct.replace(',', '.'))
    const body: CreateBillingPlanInput = {
      revenueSharePartner: partnerName.trim() ? partnerName.trim() : null,
      revenueSharePct:
        partnerName.trim() && Number.isFinite(pct) && pct > 0
          ? Math.min(pct, 100) / 100
          : null,
      milestones: validMilestones.map((m) => ({
        title: m.title.trim(),
        amountCents: parseEurosToCents(m.amount),
        vatRate: parseVatFraction(m.vat),
        dueDate: m.dueDate,
      })),
      recurrings: validRecurrings.map((r) => {
        const day = Number(r.dayOfMonth)
        const count = Number(r.quotaCount)
        return {
          name: r.name.trim(),
          concept: r.concept.trim(),
          amountCents: parseEurosToCents(r.amount),
          vatRate: parseVatFraction(r.vat),
          irpfRate: 0,
          periodicity: r.periodicity,
          dayOfMonth:
            Number.isFinite(day) && day >= 1 && day <= 28 ? Math.floor(day) : 1,
          startAnchor: (r.startAnchor === 'DATE'
            ? null
            : r.startAnchor) as CreateBillingPlanInput['recurrings'][number]['startAnchor'],
          startDate: r.startAnchor === 'DATE' ? r.startDate || null : null,
          maxOccurrences:
            r.quotaCount.trim() && Number.isFinite(count) && count > 0
              ? Math.floor(count)
              : null,
        }
      }),
    }
    setSubmitting(true)
    try {
      await api.post<CreateBillingPlanResponse>(
        `/opportunities/${opportunityId}/billing-plan`,
        body,
      )
      onCreated()
      onClose()
    } catch (err) {
      const errBody =
        err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      setError(errBody?.error ?? t('billingPlan.errorGeneric'))
    } finally {
      setSubmitting(false)
    }
  }

  // ---------- Estilos comunes ----------
  const inputCls =
    'w-full px-2.5 py-1.5 rounded-md border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:ring-1 focus:ring-toga-500/40'
  const inputStyle: React.CSSProperties = { background: 'var(--bg-input)' }

  const field = (label: string, control: React.ReactNode, spanClass = '') => (
    <div className={spanClass}>
      <label className="block text-[11px] t-secondary mb-0.5 leading-tight">{label}</label>
      {control}
    </div>
  )

  const removeBtn = (onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="w-8 h-8 grid place-items-center rounded-lg t-muted hover:text-rose-400 hover:bg-hover shrink-0"
      title={t('billingPlan.remove')}
    >
      <Trash2 size={14} />
    </button>
  )

  const addBtnCls =
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover'

  const dialog = (
    <div className="absolute inset-0 z-[100] flex flex-col" style={{ background: 'var(--bg-page)' }}>
      {/* Cabecera fija */}
      <div
        className="flex-shrink-0 px-6 py-4 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <CalendarClock size={20} className="text-toga-400 shrink-0" />
          <h3 className="text-base font-semibold t-primary truncate">
            {t('billingPlan.title')} — {opportunityTitle}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 grid place-items-center rounded-lg t-muted hover:t-primary hover:bg-hover"
          aria-label={t('billingPlan.wonPromptLater')}
        >
          <span className="text-xl leading-none">×</span>
        </button>
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-3xl mx-auto space-y-6">
          <p className="text-sm t-muted">{t('billingPlan.subtitle')}</p>

          {/* ---------- Pagos fijos (hitos) ---------- */}
          <section className="space-y-3">
            <h4 className="t-primary font-semibold text-sm">{t('billingPlan.milestonesSection')}</h4>
            {milestones.map((m, idx) => (
              <div key={idx} className="flex items-end gap-2">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_130px_150px_80px] gap-2 flex-1">
                  {field(
                    t('billingPlan.milestoneTitle'),
                    <input
                      type="text"
                      value={m.title}
                      onChange={(e) => updateMilestone(idx, { title: e.target.value })}
                      placeholder={t('billingPlan.milestoneTitlePlaceholder')}
                      className={inputCls}
                      style={inputStyle}
                    />,
                  )}
                  {field(
                    t('billingPlan.amount'),
                    <input
                      type="text"
                      inputMode="decimal"
                      value={m.amount}
                      onChange={(e) => updateMilestone(idx, { amount: e.target.value })}
                      onBlur={(e) =>
                        updateMilestone(idx, { amount: normalizeAmountInput(e.target.value) })
                      }
                      placeholder="0,00"
                      className={inputCls}
                      style={inputStyle}
                    />,
                  )}
                  {field(
                    t('billingPlan.dueDate'),
                    <input
                      type="date"
                      value={m.dueDate}
                      onChange={(e) => updateMilestone(idx, { dueDate: e.target.value })}
                      className={inputCls}
                      style={inputStyle}
                    />,
                  )}
                  {field(
                    t('billingPlan.vat'),
                    <input
                      type="text"
                      inputMode="decimal"
                      value={m.vat}
                      onChange={(e) => updateMilestone(idx, { vat: e.target.value })}
                      className={inputCls}
                      style={inputStyle}
                    />,
                  )}
                </div>
                {removeBtn(() => setMilestones((rows) => rows.filter((_, i) => i !== idx)))}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setMilestones((rows) => [
                  ...rows,
                  { title: '', amount: '', dueDate: '', vat: '21' },
                ])
              }
              className={addBtnCls}
            >
              <Plus size={14} /> {t('billingPlan.addMilestone')}
            </button>
          </section>

          {/* ---------- Cuotas recurrentes ---------- */}
          <section className="space-y-3">
            <h4 className="t-primary font-semibold text-sm">{t('billingPlan.recurringsSection')}</h4>
            {recurrings.map((r, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 border border-base rounded-xl p-3"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 flex-1">
                  {field(
                    t('billingPlan.name'),
                    <input
                      type="text"
                      value={r.name}
                      onChange={(e) => updateRecurring(idx, { name: e.target.value })}
                      placeholder={t('billingPlan.namePlaceholder')}
                      className={inputCls}
                      style={inputStyle}
                    />,
                    'sm:col-span-2',
                  )}
                  {field(
                    t('billingPlan.concept'),
                    <input
                      type="text"
                      value={r.concept}
                      onChange={(e) => updateRecurring(idx, { concept: e.target.value })}
                      placeholder={t('billingPlan.conceptPlaceholder')}
                      className={inputCls}
                      style={inputStyle}
                    />,
                    'sm:col-span-2',
                  )}
                  {field(
                    t('billingPlan.amount'),
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r.amount}
                      onChange={(e) => updateRecurring(idx, { amount: e.target.value })}
                      onBlur={(e) =>
                        updateRecurring(idx, { amount: normalizeAmountInput(e.target.value) })
                      }
                      placeholder="0,00"
                      className={inputCls}
                      style={inputStyle}
                    />,
                  )}
                  {field(
                    t('billingPlan.periodicity'),
                    <select
                      value={r.periodicity}
                      onChange={(e) =>
                        updateRecurring(idx, { periodicity: e.target.value as Periodicity })
                      }
                      className={inputCls}
                      style={inputStyle}
                    >
                      {PERIODICITIES.map((p) => (
                        <option key={p} value={p}>
                          {t(`billingPlan.periodicities.${p}`)}
                        </option>
                      ))}
                    </select>,
                  )}
                  {field(
                    t('billingPlan.dayOfMonth'),
                    <input
                      type="number"
                      min={1}
                      max={28}
                      value={r.dayOfMonth}
                      onChange={(e) => updateRecurring(idx, { dayOfMonth: e.target.value })}
                      className={inputCls}
                      style={inputStyle}
                    />,
                  )}
                  {field(
                    t('billingPlan.startAnchor'),
                    <select
                      value={r.startAnchor}
                      onChange={(e) => updateRecurring(idx, { startAnchor: e.target.value })}
                      className={inputCls}
                      style={inputStyle}
                    >
                      {START_ANCHORS.map((a) => (
                        <option key={a} value={a}>
                          {t(`serviceLineEditor.anchor.${a}`)}
                        </option>
                      ))}
                    </select>,
                  )}
                  {r.startAnchor === 'DATE'
                    ? field(
                        t('billingPlan.startDate'),
                        <input
                          type="date"
                          value={r.startDate}
                          onChange={(e) => updateRecurring(idx, { startDate: e.target.value })}
                          className={inputCls}
                          style={inputStyle}
                        />,
                      )
                    : field(
                        t('billingPlan.startDate'),
                        <div className="text-[11px] t-muted leading-tight pt-1.5">
                          {t('serviceLineEditor.anchorHint', {
                            phase: t(`serviceLineEditor.anchor.${r.startAnchor}`),
                          })}
                        </div>,
                      )}
                  {field(
                    t('billingPlan.quotaCount'),
                    <div>
                      <input
                        type="number"
                        min={1}
                        value={r.quotaCount}
                        onChange={(e) => updateRecurring(idx, { quotaCount: e.target.value })}
                        className={inputCls}
                        style={inputStyle}
                      />
                      <p className="text-[10px] t-faint mt-0.5">{t('billingPlan.quotaCountHint')}</p>
                    </div>,
                  )}
                  {field(
                    t('billingPlan.vat'),
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r.vat}
                      onChange={(e) => updateRecurring(idx, { vat: e.target.value })}
                      className={inputCls}
                      style={inputStyle}
                    />,
                  )}
                </div>
                {removeBtn(() => setRecurrings((rows) => rows.filter((_, i) => i !== idx)))}
              </div>
            ))}
            <button
              type="button"
              onClick={() => setRecurrings((rows) => [...rows, emptyRecurring()])}
              className={addBtnCls}
            >
              <Plus size={14} /> {t('billingPlan.addRecurring')}
            </button>
          </section>

          {/* ---------- Revenue share de partner ---------- */}
          <section className="space-y-3">
            <h4 className="t-primary font-semibold text-sm">{t('billingPlan.partnerSection')}</h4>
            <p className="text-xs t-muted">{t('billingPlan.partnerHint')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-2">
              {field(
                t('billingPlan.partnerName'),
                <input
                  type="text"
                  value={partnerName}
                  onChange={(e) => setPartnerName(e.target.value)}
                  placeholder={t('billingPlan.partnerNamePlaceholder')}
                  className={inputCls}
                  style={inputStyle}
                />,
              )}
              {field(
                t('billingPlan.partnerPct'),
                <input
                  type="text"
                  inputMode="decimal"
                  value={partnerPct}
                  onChange={(e) => setPartnerPct(e.target.value)}
                  placeholder="0"
                  className={inputCls}
                  style={inputStyle}
                />,
              )}
            </div>
          </section>

          {/* ---------- Resumen en vivo ---------- */}
          <section className="surface p-4 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="t-muted">{t('billingPlan.summaryContract')}</span>
              <span className="t-primary font-semibold">
                {formatCents(summary.boundedCents)}
                {summary.hasUnlimited && (
                  <span className="t-muted font-normal">
                    {' '}
                    + {t('billingPlan.unlimitedLabel')}
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="t-muted">{t('billingPlan.summaryCommission')}</span>
              <span className="t-primary font-medium">{formatCents(summary.commissionCents)}</span>
            </div>
            <div className="flex justify-between">
              <span className="t-muted">{t('billingPlan.summaryNet')}</span>
              <span className="t-primary font-semibold">
                {formatCents(summary.netCents)}
                {summary.hasUnlimited && (
                  <span className="t-muted font-normal">
                    {' '}
                    + {t('billingPlan.unlimitedLabel')}
                  </span>
                )}
              </span>
            </div>
          </section>

          {error && (
            <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Pie fijo */}
      <div
        className="flex-shrink-0 px-6 py-4 border-t flex items-center justify-end gap-2"
        style={{ borderColor: 'var(--border)' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void onSubmit()}
          disabled={submitting}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-1.5"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitting ? t('billingPlan.creating') : t('billingPlan.create')}
        </button>
      </div>
    </div>
  )

  return createPortal(dialog, getContainer())
}
